import { Services } from "slotworks/services/services";
import ControlOptions from "slotworks/smart-ui/options/control-options";
import ValueList from "slotworks/utils/value-list";

export enum RealityMode {
    FREE = "free_play",
    REAL = "real_play"
}

class SettingsModel {
    private static SPACE_TO_SPIN: string = "spaceToSpin";
    private static DISPLAY_CREDITS: string = "displayCredits";
    private static SHOW_INTRO: string = "showIntro";
    private static QUICK_SPIN: string = "quickSpin";

    public playMode: RealityMode = RealityMode.FREE;
    public language: string = "en";
    public homeURL: string = "#";
    public historyURL: string = "#";
    public onSessionClose: Function;

    public sessionLimit: ValueList<number> = new ValueList();
    public sessionStartTime: number;

    public savePreferences: boolean = true;

    // These are private so getters must be used, giving us the opportunity to save to local storage
    private spaceToSpin: boolean = true;
    private displayCredits: boolean = false;
    private showIntro: boolean = true;
    private quickSpin: boolean = false;
    private turboSpin: boolean = false;

    private isLoaded: boolean;

    constructor() {
        this.sessionLimit.defaultValue = 0;
    }

    public startSession() {
        this.sessionStartTime = Date.now();
    }

    public getSessionLength(): number {
        const timeNow = Date.now();

        return Math.round((timeNow - this.sessionStartTime) / 60000);
    }

    public setSpaceToSpin(value: boolean) {
        this.spaceToSpin = value;
        this.save();
    }

    public getSpaceToSpin() {
        return this.spaceToSpin;
    }

    public getDisplayCredits() {
        return this.displayCredits;
    }

    public getQuickSpin() {
        return this.quickSpin;
    }

    public getTurboSpin() {
        return this.turboSpin;
    }

    public setTurboSpin(value: boolean) {
        this.turboSpin = value;
    }

    public setQuickSpin(value: boolean) {
        this.quickSpin = value;
    }

    public getShowIntro() {
        return this.showIntro;
    }

    public load() {
        const displayCredits = Services.storage.getBoolean(SettingsModel.DISPLAY_CREDITS);
        const showIntro = Services.storage.getBoolean(SettingsModel.SHOW_INTRO);
        const spaceToSpin = Services.storage.getBoolean(SettingsModel.SPACE_TO_SPIN);
        const quickSpin = Services.storage.getBoolean(SettingsModel.QUICK_SPIN);

        if (spaceToSpin !== null) {
            this.spaceToSpin = spaceToSpin;
        }

        if (displayCredits !== null) {
            this.setDisplayCredits(displayCredits);
        }

        if (showIntro !== null) {
            this.showIntro = showIntro;
        }

        if (quickSpin !== null) {
            this.quickSpin = quickSpin;
        }

        this.isLoaded = true;
    }

    public updateFromOptions(options: ControlOptions) {
        if (options.sessionLimit.hasData() && (options.sessionLimit.currentValue < this.getSessionLength() || this.sessionLimit.currentValue === 0)) {
            this.startSession();
        }
        if (options.sessionLimit.hasData()) {
            this.sessionLimit = options.sessionLimit.clone();
        }
        if (options.displayCredits !== undefined) {
            this.setDisplayCredits(options.displayCredits);
        }
        if (options.showIntro !== undefined) {
            this.showIntro = options.showIntro;
        }

        if (options.quickSpin !== undefined) {
            this.quickSpin = options.quickSpin;
        }
        if (options.turboSpin !== undefined) {
            this.turboSpin = options.turboSpin;
        }
        if (options.spaceToSpin !== undefined) {
            this.spaceToSpin = options.spaceToSpin;
        }

        this.save();
    }

    public injectGeneralOptions(options: ControlOptions) {
        options.displayCredits = this.getDisplayCredits();
        options.showIntro = this.getShowIntro();
        options.spaceToSpin = this.spaceToSpin;

        options.quickSpin = this.quickSpin;
        options.turboSpin = this.turboSpin;
        options.sessionLimit = this.sessionLimit.clone();
        options.homeURL = this.homeURL;
        options.historyURL = this.historyURL;
        options.onSessionClose = this.onSessionClose;
    }

    public save() {
        if (this.isLoaded && this.savePreferences) {
            Services.storage.set(SettingsModel.DISPLAY_CREDITS, this.displayCredits);
            Services.storage.set(SettingsModel.SHOW_INTRO, this.showIntro);
            Services.storage.set(SettingsModel.SPACE_TO_SPIN, this.spaceToSpin);
            Services.storage.set(SettingsModel.QUICK_SPIN, this.quickSpin);
        }
    }

    public setDisplayCredits(value: boolean) {
        this.displayCredits = value;
        Services.currency.credits = this.displayCredits;
        this.save();
    }
}

const settingsModel = new SettingsModel();

export default settingsModel;
